import React from 'react';

import {assert} from 'Asserts';
import {ProcedureRunDataEntryMode, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import {createProcedureRunPlans} from 'procedures/network/procedureRunPlanDAO';
import EntityStore from 'stores/EntityStore';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {refreshStudyOverview} from 'studies/sidepanel/TrayTaskUtils';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {getTypedStudyAdapter} from '../TypedStudyAdapters';

function getDefaultDataEntryMode(studyType: StudyType): ProcedureRunDataEntryMode {
  const isMultiBatchEnabled = getTypedStudyAdapter(studyType).isMultiBatchEnabled;
  // PD studies default to parallel execution; other study types enter condition replicates individually.
  return isMultiBatchEnabled
    ? ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER
    : ProcedureRunDataEntryMode.INDIVIDUAL_CONDITION_REPLICATES;
}

/**
 * Creates a procedure-less procedure run plan (no linked flowchart version) and navigates directly to
 * its editor, where the user selects a procedure to attach. Used behind
 * STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN in place of opening AddProcedureRunPlanModal.
 */
export async function createProcedureRunPlanAndNavigate(
  studyId: string,
  studyType: StudyType
): Promise<void> {
  const currentUser = EntityStore.currentUser();
  assert(currentUser != null, 'Expected current user to be defined');

  try {
    const response = await withLoadingScreen(
      createProcedureRunPlans(studyId, [
        {
          description: '',
          creatorId: currentUser.id,
          dataEntryMode: getDefaultDataEntryMode(studyType),
        },
      ]),
      `Creating ${getProcedureRunUIStringForStudyType(studyType)} plan...`
    );

    refreshStudyOverview(studyId, studyType);

    const createdPlanId = response.createdIds[0];
    if (createdPlanId) {
      await navigateToStudyOverviewTab(studyId, createdPlanId);
    }
  } catch (e) {
    if (net.isServerRequestError(e)) {
      notifications.error(
        <ToastMessage
          header={`Failed to create ${getProcedureRunUIStringForStudyType(studyType)} plan`}
          body={notifications.getFriendlyErrorAsElement(e, '')}
        />
      );
    } else {
      throw e;
    }
  }
}
